import { Alert, Button, Divider, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { useAutojobNextRun } from 'src/shared/hooks/useAutojobNextRun';

export type FailedAutogiroPaymentMailValues = {
  autogiroFailedPaymentMailEnabled: boolean;
  autogiroFailedRetrySubject: string;
  autogiroFailedRetryBody: string;
  autogiroFailedFinalSubject: string;
  autogiroFailedFinalBody: string;
  autogiroFailedMandateSubject: string;
  autogiroFailedMandateBody: string;
};

type FailedAutogiroPaymentMailSettingsProps = {
  initialValues: Partial<FailedAutogiroPaymentMailValues>;
  // Platshållare som kan användas i ämne och text.
  customFields: { label: string; value: string }[];
  // Översättningsprefix, t.ex. 'invoice:failedAutogiroPaymentMail'.
  translationPrefix: string;
  onSave: (settings: FailedAutogiroPaymentMailValues) => Promise<unknown>;
};

type FailedAutogiroPaymentMailForm = {
  retrySubject: string;
  retryBody: string;
  finalSubject: string;
  finalBody: string;
  mandateSubject: string;
  mandateBody: string;
};

type MailVariant = 'retry' | 'final' | 'mandate';

/**
 * Mailen som skickas till medlemmen när en autogirodragning misslyckas.
 *
 * Tre texter, eftersom beskedet är olika. Bankgirot talar om ifall ett nytt försök kommer,
 * och först när försöken är slut är det meningsfullt att be medlemmen betala själv.
 */
export const FailedAutogiroPaymentMailSettings = (props: FailedAutogiroPaymentMailSettingsProps) => {
  const { initialValues, customFields, translationPrefix, onSave } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const tk = (key: string) => t(`${translationPrefix}.${key}`);

  const [mailEnabled, setMailEnabled] = useState(initialValues.autogiroFailedPaymentMailEnabled ?? false);

  const formMethods = useForm<FailedAutogiroPaymentMailForm>({
    mode: 'all',
    defaultValues: {
      retrySubject: initialValues.autogiroFailedRetrySubject ?? '',
      retryBody: initialValues.autogiroFailedRetryBody ?? '',
      finalSubject: initialValues.autogiroFailedFinalSubject ?? '',
      finalBody: initialValues.autogiroFailedFinalBody ?? '',
      mandateSubject: initialValues.autogiroFailedMandateSubject ?? '',
      mandateBody: initialValues.autogiroFailedMandateBody ?? '',
    },
  });

  const { handleSubmit } = formMethods;

  // Tiden läses från jobbet, så den stämmer även om schemat ändrats i efterhand.
  const nextRun = useAutojobNextRun('SendEmailFailedAutogiroPaymentMember', mailEnabled);

  const onSubmit = async (data: FailedAutogiroPaymentMailForm) => {
    try {
      await onSave({
        autogiroFailedPaymentMailEnabled: mailEnabled,
        autogiroFailedRetrySubject: data.retrySubject,
        autogiroFailedRetryBody: data.retryBody,
        autogiroFailedFinalSubject: data.finalSubject,
        autogiroFailedFinalBody: data.finalBody,
        autogiroFailedMandateSubject: data.mandateSubject,
        autogiroFailedMandateBody: data.mandateBody,
      });
      toast.success(t('common:success.save') as string);
    } catch {
      toast.error(t('common:error.save') as string);
    }
  };

  const renderMail = (variant: MailVariant) => (
    <Grid item xs={12}>
      <ContentSection>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant='subtitle1' fontWeight='bold'>
              {tk(variant)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {tk(`${variant}Description`)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <ControlledTextField name={`${variant}Subject`} label={tk('subject')} size='small' fullWidth sx={{ mt: 1, mb: 2 }} />
            <ControlledTextEditor name={`${variant}Body`} rows={30} noMargin customFields={customFields} />
          </Grid>
        </Grid>
      </ContentSection>
    </Grid>
  );

  return (
    <Grid item xs={12}>
      <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
        {tk('heading')}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={mailEnabled} onChange={(e) => setMailEnabled(e.target.checked)} />} label={tk('enable')} />
            </Grid>

            {mailEnabled && (
              <>
                <Grid item xs={12} sx={{ pt: '0 !important' }}>
                  <Typography variant='body2' color='text.secondary'>
                    {nextRun ? tk('scheduleWithNextRun').replace('{{nextRun}}', nextRun) : tk('schedule')}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Alert severity='info'>{tk('availableFields')}</Alert>
                </Grid>

                {renderMail('retry')}

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {renderMail('final')}

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {renderMail('mandate')}
              </>
            )}

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant='contained' disableElevation type='submit'>
                {t('common:button.save')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Grid>
  );
};
